<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable no-use-before-define */
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import ImovelAPI from 'dashboard/api/imoveis';
import ContactFavoriteImoveisAPI from 'dashboard/api/contactFavoriteImoveis';
import FavoriteListsAPI from 'dashboard/api/favoriteLists';
import RecentSearchesAPI from 'dashboard/api/recentSearches';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTracking } from 'dashboard/composables';
import { buildRealEstateCapabilityContext } from 'dashboard/helper/permissionsHelper';
import { REAL_ESTATE_SURFACES } from 'dashboard/constants/permissions';
import {
  detailIdForListing,
  listingCardTrackingPayload,
  normalizeListingCards,
  publicUrlForListing,
  valueFrom,
} from 'dashboard/helper/lais/listingCardPresenter';
import {
  LAIS_TRACKING_EVENTS,
  quickFilterTrackingPayload,
} from 'dashboard/helper/lais/trackingEvents';
import {
  IMOVEL_STATUS,
  IMOVEL_STATUS_LABELS,
} from 'dashboard/constants/lais/imovel';

import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisFavoritesBadge from 'dashboard/components-next/LaisFavoritesBadge.vue';
import LaisOperationalPage from 'dashboard/components-next/LaisOperationalPage.vue';
import LaisFilterButton from 'dashboard/components-next/LaisFilterButton.vue';
import LaisFilterBar from 'dashboard/components-next/LaisFilterBar.vue';
import LaisActionBar from 'dashboard/components-next/LaisActionBar.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisMetricStrip from 'dashboard/components-next/LaisMetricStrip.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisRunStatusPanel from 'dashboard/components-next/LaisRunStatusPanel.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisRecentSearches from 'dashboard/components-next/LaisRecentSearches.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';
import LaisDiscoveryState from 'dashboard/components-next/LaisDiscoveryState.vue';
import LaisCockpitLocation from 'dashboard/components-next/lais/LaisCockpitLocation.vue';
import ListingDiscoveryCard from 'dashboard/components-next/listing/ListingDiscoveryCard.vue';
import MobileHouseList from 'dashboard/components-next/listing/MobileHouseList.vue';
import SearchCarousel from 'dashboard/components-next/listing/SearchCarousel.vue';
import SearchSideList from 'dashboard/components-next/listing/SearchSideList.vue';
import OrganizeFavoritesDialog from 'dashboard/routes/dashboard/laisFavorites/components/OrganizeFavoritesDialog.vue';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const { track } = useTracking();

const CATALOG_MODES = ['properties', 'empreendimentos'];
const FINALIDADE_FILTERS = ['todos', 'venda', 'aluguel', 'temporada'];
const STATUS_FILTERS = Object.values(IMOVEL_STATUS);
const VIEW_MODES = ['list', 'side', 'map'];
const HIGHLIGHT_PLACEMENTS = [
  { value: 'vitrine', labelKey: 'PROPERTIES.HIGHLIGHT.PLACEMENTS.VITRINE' },
  { value: 'destaque', labelKey: 'PROPERTIES.HIGHLIGHT.PLACEMENTS.DESTAQUE' },
  {
    value: 'super_destaque',
    labelKey: 'PROPERTIES.HIGHLIGHT.PLACEMENTS.SUPER',
  },
];
const DEFAULT_OPERATION_MODE = 'importacao';
const OPERATION_MODES = [
  {
    value: DEFAULT_OPERATION_MODE,
    label: 'Importação',
    helper: 'XML, status do último run e console de reprocessamento',
    icon: 'i-lucide-file-up',
    capability: 'import_console',
  },
  {
    value: 'publicacao',
    label: 'Publicação',
    helper: 'Publicáveis, portais e fila operacional',
    icon: 'i-lucide-radio-tower',
    capability: 'publication_queue',
  },
  {
    value: 'destaque_programado',
    label: 'Destaque programado',
    helper: 'Agendamento em lote para imóveis disponíveis',
    icon: 'i-lucide-sparkles',
    capability: 'bulk_highlight',
  },
];
const OPERATION_MODE_VALUES = OPERATION_MODES.map(mode => mode.value);
const HIGHLIGHTABLE_STATUSES = [
  IMOVEL_STATUS.DISPONIVEL,
  'anunciado',
  'available',
];
const IMPORT_STATUS_META = Object.freeze({
  enqueued: {
    tone: 'warning',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.ENQUEUED',
  },
  processing: {
    tone: 'info',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.PROCESSING',
  },
  running: {
    tone: 'info',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.PROCESSING',
  },
  completed: {
    tone: 'positive',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.COMPLETED',
  },
  processed: {
    tone: 'positive',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.COMPLETED',
  },
  failed: {
    tone: 'danger',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.FAILED',
  },
  cancelled: {
    tone: 'neutral',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.CANCELLED',
  },
  idle: {
    tone: 'neutral',
    labelKey: 'PROPERTIES.OPERATIONS.IMPORT.STATUS.IDLE',
  },
});
const routeValue = value => (Array.isArray(value) ? value[0] : value);
const routeString = value => String(routeValue(value) || '');
const routePositiveNumber = (value, fallback) => {
  const parsed = Number(routeValue(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const routeBoolean = value =>
  ['1', 'true', 'sim', 'yes'].includes(
    String(routeValue(value) || '').toLowerCase()
  );
const routeValueFromList = (value, list, fallback) => {
  const normalized = routeString(value);
  return list.includes(normalized) ? normalized : fallback;
};
const businessContextFromFinalidade = finalidade =>
  ({
    venda: 'purchase',
    aluguel: 'rental',
    temporada: 'rental',
  })[finalidade] || '';
const finalidadeFromBusinessContext = context =>
  ({
    purchase: 'venda',
    sale: 'venda',
    venda: 'venda',
    rental: 'aluguel',
    rent: 'aluguel',
    aluguel: 'aluguel',
  })[String(context || '').toLowerCase()] || '';
const catalogModeFromRoute = () => {
  const catalogFromQuery = routeValueFromList(
    route.query.catalog,
    CATALOG_MODES,
    ''
  );
  if (catalogFromQuery) return catalogFromQuery;
  return route.path.includes('/empreendimentos')
    ? 'empreendimentos'
    : 'properties';
};
const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );
const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${Array.isArray(value) ? value[0] : value}`)
    .join('|');

const isLoading = ref(false);
const page = ref(routePositiveNumber(route.query.page, 1));
const perPage = ref(routePositiveNumber(route.query.per_page, 10));
const selected = ref(new Set());
const showFilters = ref(false);
const errorMessage = ref('');
const activeListingId = ref('');
const viewedCardKeys = ref(new Set());
const listingResultsEl = ref(null);

const catalogMode = ref(catalogModeFromRoute());
const viewMode = ref(routeValueFromList(route.query.view, VIEW_MODES, 'list'));
const qFilter = ref(routeString(route.query.q));
const finalidadeFilter = ref(
  routeValueFromList(
    route.query.finalidade,
    FINALIDADE_FILTERS,
    finalidadeFromBusinessContext(route.query.business_context) || 'todos'
  )
);
const cidadeFilter = ref(routeString(route.query.cidade));
const bairroFilter = ref(routeString(route.query.bairro));
const locationSearchQuery = ref(
  [cidadeFilter.value, bairroFilter.value].filter(Boolean).join(', ')
);
const tipoFilter = ref(routeString(route.query.tipo));
const quartosMinFilter = ref(routeString(route.query.quartos_min));
const portalOrigemFilter = ref(routeString(route.query.portal_origem));
const statusFilter = ref(
  routeValueFromList(route.query.status, STATUS_FILTERS, '')
);
const apenasDisponiveis = ref(routeBoolean(route.query.apenas_disponiveis));
const recentSearches = ref([]);
const favoritesCount = ref(0);
const imoveis = ref([]);
const meta = ref({});
const importStatusPayload = ref(null);
const importLoading = ref(false);
const importError = ref('');
const capabilityError = ref(null);
const showBulkFavoritesDialog = ref(false);
const bulkFavoriteSaving = ref(false);
const bulkFavoriteLists = ref([]);
const bulkFavoriteListsLoading = ref(false);
const highlightSubmitting = ref(false);
const highlightError = ref('');
const highlightForm = reactive({
  scheduledFor: '',
  placement: 'vitrine',
  notes: '',
});
const hasLoadedCatalogAfterPermission = ref(false);
let isSyncingRouteState = false;

const totalCount = computed(
  () => meta.value.count || meta.value.totalCount || imoveis.value.length
);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalCount.value / perPage.value))
);
const activeContactId = computed(() => {
  const rawValue = route.query.contact_id;
  const normalized = Number(rawValue);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
});
const preferredFavoriteListName = computed(() =>
  routeString(route.query.favorite_list)
);
const activeFilterSlug = computed(
  () => route.query.slug?.toString() || buildFilterSlug()
);
const catalogOptions = [
  {
    value: 'properties',
    label: 'Imóveis',
    helper: 'Base integrada de imóveis',
    icon: 'i-lucide-home',
  },
  {
    value: 'empreendimentos',
    label: 'Empreendimentos',
    helper: 'Imudei',
    icon: 'i-lucide-building-2',
  },
];
const headerHelper = computed(() =>
  activeContactId.value
    ? 'Consulte imóveis reais e empreendimentos para reaplicar buscas deste cliente.'
    : 'Consulte a prateleira real de imóveis e empreendimentos integrada aos seus sistemas.'
);
const pageTitle = computed(
  () =>
    catalogOptions.find(option => option.value === catalogMode.value)?.label ||
    'Imóveis'
);
const hasActiveFilters = computed(
  () =>
    showFilters.value ||
    !!qFilter.value ||
    (finalidadeFilter.value && finalidadeFilter.value !== 'todos') ||
    !!cidadeFilter.value ||
    !!bairroFilter.value ||
    !!tipoFilter.value ||
    !!quartosMinFilter.value ||
    !!portalOrigemFilter.value ||
    !!statusFilter.value ||
    apenasDisponiveis.value
);
const listingCards = computed(() =>
  normalizeListingCards(imoveis.value, {
    catalogMode: catalogMode.value,
    selectedIds: selected.value,
  })
);
const hasListings = computed(() => listingCards.value.length > 0);
const showMapMode = computed(() => viewMode.value === 'map');
const desktopListMode = computed(() =>
  viewMode.value === 'side' || viewMode.value === 'map'
    ? viewMode.value
    : 'list'
);
const resultKindLabel = computed(() => {
  if (catalogMode.value === 'empreendimentos') {
    return totalCount.value === 1
      ? t('PROPERTIES.LIST.RESULT.DEVELOPMENT_SINGULAR')
      : t('PROPERTIES.LIST.RESULT.DEVELOPMENT_PLURAL');
  }

  return totalCount.value === 1
    ? t('PROPERTIES.LIST.RESULT.PROPERTY_SINGULAR')
    : t('PROPERTIES.LIST.RESULT.PROPERTY_PLURAL');
});
const emptyStateLabel = computed(() => {
  if (finalidadeFilter.value === 'aluguel') {
    return t('PROPERTIES.LIST.EMPTY_RENT');
  }
  if (finalidadeFilter.value === 'venda') {
    return t('PROPERTIES.LIST.EMPTY_SALE');
  }
  return t('PROPERTIES.LIST.EMPTY_GENERIC');
});
const emptyStateDescription = computed(() =>
  hasActiveFilters.value
    ? t('PROPERTIES.LIST.EMPTY_DESCRIPTION_FILTERED')
    : t('PROPERTIES.LIST.EMPTY_DESCRIPTION_DEFAULT')
);
const selectedLocationArea = computed(() => {
  const label = [bairroFilter.value, cidadeFilter.value]
    .filter(Boolean)
    .join(', ');
  if (!label) return null;

  return {
    label,
    slug: buildFilterSlug(),
    cidade: cidadeFilter.value,
    bairro: bairroFilter.value,
  };
});
const capabilitiesContext = computed(() =>
  buildRealEstateCapabilityContext({
    surface: REAL_ESTATE_SURFACES.IMOVEIS_INDEX,
    user: currentUser.value,
    accountId: accountId.value,
    meta: meta.value,
    error: capabilityError.value,
  })
);
const capabilityFor = action =>
  capabilitiesContext.value.actions[action] || {
    visible: false,
    disabled: true,
    allowed: false,
  };
const canViewImoveis = computed(() => capabilityFor('view').visible);
const canUseImoveisFilters = computed(() => capabilityFor('filters').visible);
const canCreateImovel = computed(() => capabilityFor('create').visible);
const canCreateImovelAction = computed(() => capabilityFor('create').allowed);
const canOpenImportConsole = computed(
  () => capabilityFor('import_console').visible
);
const canOpenImportConsoleAction = computed(
  () => capabilityFor('import_console').allowed
);
const canStartImport = computed(() => capabilityFor('import_start').visible);
const canStartImportAction = computed(
  () => capabilityFor('import_start').allowed
);
const canReviewDuplicates = computed(
  () => capabilityFor('duplicate_candidates').visible
);
const canReviewDuplicatesAction = computed(
  () => capabilityFor('duplicate_candidates').allowed
);
const canReviewPortalSync = computed(
  () => capabilityFor('portal_sync_operations').visible
);
const canReviewPortalSyncAction = computed(
  () => capabilityFor('portal_sync_operations').allowed
);
const canReviewPublicationQueue = computed(
  () => capabilityFor('publication_queue').visible
);
const canReviewPublicationQueueAction = computed(
  () => capabilityFor('publication_queue').allowed
);
const canManageBulkHighlight = computed(
  () => capabilityFor('bulk_highlight').visible
);
const canManageBulkHighlightAction = computed(
  () => capabilityFor('bulk_highlight').allowed
);
const visibleOperationModes = computed(() =>
  OPERATION_MODES.map(mode => {
    const capability = capabilityFor(mode.capability);
    return {
      ...mode,
      disabled: !capability.allowed,
      deniedBy: capability.deniedBy,
      visible: capability.visible,
    };
  }).filter(mode => mode.visible)
);
const hasOperationalActions = computed(
  () => visibleOperationModes.value.length > 0
);
const activeOperationMode = computed(() => {
  const requested = routeValueFromList(
    route.query.operacao,
    OPERATION_MODE_VALUES,
    DEFAULT_OPERATION_MODE
  );

  if (visibleOperationModes.value.some(mode => mode.value === requested)) {
    return requested;
  }

  return visibleOperationModes.value[0]?.value || DEFAULT_OPERATION_MODE;
});
const capabilityNotice = computed(() => {
  const { state } = capabilitiesContext.value;

  if (state.permissionDenied) {
    return null;
  }
  if (state.stale) {
    return {
      tone: 'warning',
      title: 'Dados desatualizados',
      description:
        'Os dados desta lista podem estar defasados. Atualize antes de publicar, importar ou destacar imóveis.',
    };
  }
  if (state.partial) {
    return {
      tone: 'warning',
      title: 'Resultado parcial',
      description:
        'Parte das fontes não respondeu. Filtros seguem disponíveis, mas ações operacionais ficam bloqueadas.',
    };
  }

  return null;
});

const businessContextTabs = [
  { value: 'todos', label: 'Todos', helper: 'Alugar e comprar' },
  { value: 'aluguel', label: 'Alugar', helper: 'Aluguel' },
  { value: 'venda', label: 'Comprar', helper: 'Valor de venda' },
];
const statusOptions = STATUS_FILTERS.map(value => ({
  value,
  label: IMOVEL_STATUS_LABELS[value] || value,
}));

const fetchImoveis = async ({ syncRoute = true } = {}) => {
  if (!canViewImoveis.value) {
    imoveis.value = [];
    meta.value = {};
    capabilityError.value = { status: 403 };
    errorMessage.value = capabilityNotice.value?.description || '';
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';
  try {
    if (syncRoute) syncRouteState();

    const businessContext =
      businessContextFromFinalidade(finalidadeFilter.value) ||
      routeString(route.query.business_context);
    const params = {
      page: page.value,
      per_page: perPage.value,
      sort: routeString(route.query.sort) || 'relevance:desc',
    };
    if (qFilter.value) params.q = qFilter.value;
    if (finalidadeFilter.value && finalidadeFilter.value !== 'todos') {
      params.finalidade = finalidadeFilter.value;
    }
    if (businessContext) params.business_context = businessContext;
    if (routeString(route.query.similar_to)) {
      params.similar_to = routeString(route.query.similar_to);
    }
    if (cidadeFilter.value) params.cidade = cidadeFilter.value;
    if (bairroFilter.value) params.bairro = bairroFilter.value;
    if (tipoFilter.value) params.tipo = tipoFilter.value;
    if (quartosMinFilter.value) params.quartos_min = quartosMinFilter.value;
    if (portalOrigemFilter.value) {
      params.portal_origem = portalOrigemFilter.value;
    }
    if (statusFilter.value) params.status = statusFilter.value;
    if (apenasDisponiveis.value) params.apenas_disponiveis = true;
    const fetcher =
      catalogMode.value === 'empreendimentos'
        ? ImovelAPI.empreendimentos
        : ImovelAPI.externalProperties;
    const { data } = await fetcher.call(ImovelAPI, params);
    imoveis.value = camelcaseKeys(data.payload || [], { deep: true });
    meta.value = camelcaseKeys(data.meta || {}, { deep: true });
    capabilityError.value = null;
    const firstCard = normalizeListingCards(imoveis.value, {
      catalogMode: catalogMode.value,
      selectedIds: selected.value,
    })[0];
    if (!activeListingId.value || !imoveis.value.length) {
      activeListingId.value = firstCard?.id || '';
    }
  } catch (error) {
    capabilityError.value = error;
    errorMessage.value = t('PROPERTIES.LIST.LOAD_ERROR');
    imoveis.value = [];
    meta.value = {};
    useAlert(errorMessage.value);
  } finally {
    isLoading.value = false;
  }
};

const buildFilterSlug = () => {
  const segments = [
    catalogMode.value,
    qFilter.value || null,
    finalidadeFilter.value !== 'todos' ? finalidadeFilter.value : null,
    tipoFilter.value || null,
    cidadeFilter.value || null,
    bairroFilter.value || null,
    statusFilter.value || null,
    quartosMinFilter.value ? `${quartosMinFilter.value}-quartos` : null,
    apenasDisponiveis.value ? 'disponiveis' : null,
  ]
    .filter(Boolean)
    .map(segment =>
      segment
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
    );

  return segments.join('__') || 'todos-os-imoveis';
};

const syncRouteState = ({
  slug = buildFilterSlug(),
  operationMode = routeString(route.query.operacao),
} = {}) => {
  const businessContext = businessContextFromFinalidade(finalidadeFilter.value);
  const nextQuery = compactQuery({
    ...(route.query || {}),
    catalog: catalogMode.value === 'properties' ? undefined : catalogMode.value,
    slug,
    operacao: operationMode || undefined,
    q: qFilter.value.trim(),
    finalidade:
      finalidadeFilter.value === 'todos' ? undefined : finalidadeFilter.value,
    cidade: cidadeFilter.value.trim(),
    bairro: bairroFilter.value.trim(),
    tipo: tipoFilter.value.trim(),
    quartos_min: quartosMinFilter.value,
    portal_origem: portalOrigemFilter.value.trim(),
    status: statusFilter.value,
    apenas_disponiveis: apenasDisponiveis.value ? 'true' : undefined,
    business_context: businessContext || undefined,
    sort: routeString(route.query.sort) || 'relevance:desc',
    view: viewMode.value === 'list' ? undefined : viewMode.value,
    page: page.value === 1 ? undefined : page.value,
    per_page: perPage.value === 10 ? undefined : perPage.value,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  isSyncingRouteState = true;
  router
    .replace({ path: route.path, query: nextQuery })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingRouteState = false;
      }, 0);
    });
};

const applyRouteState = () => {
  page.value = routePositiveNumber(route.query.page, 1);
  perPage.value = routePositiveNumber(route.query.per_page, 10);
  catalogMode.value = catalogModeFromRoute();
  viewMode.value = routeValueFromList(route.query.view, VIEW_MODES, 'list');
  qFilter.value = routeString(route.query.q);
  finalidadeFilter.value = routeValueFromList(
    route.query.finalidade,
    FINALIDADE_FILTERS,
    finalidadeFromBusinessContext(route.query.business_context) || 'todos'
  );
  cidadeFilter.value = routeString(route.query.cidade);
  bairroFilter.value = routeString(route.query.bairro);
  locationSearchQuery.value = [cidadeFilter.value, bairroFilter.value]
    .filter(Boolean)
    .join(', ');
  tipoFilter.value = routeString(route.query.tipo);
  quartosMinFilter.value = routeString(route.query.quartos_min);
  portalOrigemFilter.value = routeString(route.query.portal_origem);
  statusFilter.value = routeValueFromList(
    route.query.status,
    STATUS_FILTERS,
    ''
  );
  apenasDisponiveis.value = routeBoolean(route.query.apenas_disponiveis);
  selected.value = new Set();
};

const currentFilterPayload = () => ({
  catalog: catalogMode.value,
  q: qFilter.value,
  finalidade: finalidadeFilter.value,
  cidade: cidadeFilter.value,
  bairro: bairroFilter.value,
  tipo: tipoFilter.value,
  quartos_min: quartosMinFilter.value,
  portal_origem: portalOrigemFilter.value,
  status: statusFilter.value,
  apenas_disponiveis: apenasDisponiveis.value,
  business_context: businessContextFromFinalidade(finalidadeFilter.value),
});

const buildRecentSearchLabel = filters => {
  const parts = [];

  if (filters.finalidade && filters.finalidade !== 'todos') {
    parts.push(
      {
        venda: 'Venda',
        aluguel: 'Aluguel',
        temporada: 'Temporada',
      }[filters.finalidade] || filters.finalidade
    );
  }

  if (filters.q) {
    parts.push(filters.q);
  }

  if (filters.tipo) {
    parts.push(filters.tipo);
  }

  if (filters.status) {
    parts.push(IMOVEL_STATUS_LABELS[filters.status] || filters.status);
  }

  if (filters.cidade) {
    parts.push(filters.cidade);
  }

  if (filters.bairro) {
    parts.push(filters.bairro);
  }

  if (filters.apenas_disponiveis) {
    parts.push('Disponíveis');
  }

  return parts.join(' · ') || 'Busca geral';
};

const trackAnalyticsEvent = (name, properties = {}) => {
  if (!accountId.value) return null;

  return track(name, {
    accountId: accountId.value,
    ...properties,
  });
};

const fetchLeadContext = async () => {
  if (!activeContactId.value) {
    recentSearches.value = [];
    favoritesCount.value = 0;
    return;
  }

  const [searchesResult, favoritesResult] = await Promise.allSettled([
    RecentSearchesAPI.get(activeContactId.value),
    ContactFavoriteImoveisAPI.get(activeContactId.value),
  ]);

  if (searchesResult.status === 'fulfilled') {
    recentSearches.value = searchesResult.value.data?.data || [];
  } else {
    recentSearches.value = [];
  }

  if (favoritesResult.status === 'fulfilled') {
    favoritesCount.value = favoritesResult.value.data?.meta?.count || 0;
  } else {
    favoritesCount.value = 0;
  }
};

const persistRecentSearch = async slug => {
  if (!activeContactId.value) return;

  try {
    await RecentSearchesAPI.create(activeContactId.value, {
      label: buildRecentSearchLabel(currentFilterPayload()),
      slug,
      filters: currentFilterPayload(),
    });
    await fetchLeadContext();
  } catch {
    // no-op
  }
};

const openFavoritesForLead = () => {
  if (!activeContactId.value) return;

  router.push({
    name: 'favoritos_index',
    params: { accountId: accountId.value },
    query: { contact_id: String(activeContactId.value) },
  });
};

const applyFilters = ({
  operationMode = routeString(route.query.operacao),
} = {}) => {
  page.value = 1;
  const slug = buildFilterSlug();
  syncRouteState({ slug, operationMode });
  trackAnalyticsEvent(
    LAIS_TRACKING_EVENTS.LISTING_QUICK_FILTER_CLICKED,
    quickFilterTrackingPayload(slug, {
      slug,
      ...currentFilterPayload(),
    })
  );
  persistRecentSearch(slug);
  fetchImoveis({ syncRoute: false });
};

const clearFilters = () => {
  qFilter.value = '';
  finalidadeFilter.value = 'todos';
  cidadeFilter.value = '';
  bairroFilter.value = '';
  locationSearchQuery.value = '';
  tipoFilter.value = '';
  quartosMinFilter.value = '';
  portalOrigemFilter.value = '';
  statusFilter.value = '';
  apenasDisponiveis.value = false;
  applyFilters();
};

const setCatalogMode = mode => {
  if (catalogMode.value === mode) return;
  catalogMode.value = mode;
  selected.value = new Set();
  applyFilters();
};

const setBusinessContext = context => {
  if (finalidadeFilter.value === context) return;
  finalidadeFilter.value = context;
  applyFilters();
};

const applyRecentSearch = search => {
  const filters = search?.filters || {};

  catalogMode.value = filters.catalog || 'properties';
  qFilter.value = filters.q || '';
  finalidadeFilter.value = filters.finalidade || 'todos';
  cidadeFilter.value = filters.cidade || '';
  bairroFilter.value = filters.bairro || '';
  locationSearchQuery.value = [cidadeFilter.value, bairroFilter.value]
    .filter(Boolean)
    .join(', ');
  tipoFilter.value = filters.tipo || '';
  quartosMinFilter.value = filters.quartos_min || '';
  portalOrigemFilter.value = filters.portal_origem || '';
  statusFilter.value = STATUS_FILTERS.includes(filters.status)
    ? filters.status
    : '';
  apenasDisponiveis.value = Boolean(filters.apenas_disponiveis);

  page.value = 1;
  const slug = search.slug || buildFilterSlug();
  syncRouteState({ slug });

  trackAnalyticsEvent(
    LAIS_TRACKING_EVENTS.LISTING_QUICK_FILTER_CLICKED,
    quickFilterTrackingPayload(slug, {
      ...(filters || {}),
      slug,
      source: 'recent_search',
    })
  );
  fetchImoveis({ syncRoute: false });
};

const removeRecentSearch = async search => {
  if (!activeContactId.value || !search?.id) return;

  try {
    await RecentSearchesAPI.delete(activeContactId.value, search.id);
    recentSearches.value = recentSearches.value.filter(
      currentSearch => currentSearch.id !== search.id
    );
  } catch {
    useAlert('Não foi possível remover a busca recente.');
  }
};

const handleLocationSelect = area => {
  locationSearchQuery.value = area?.label || area?.full_address || '';
  cidadeFilter.value = area?.cidade || cidadeFilter.value;
  bairroFilter.value = area?.bairro || bairroFilter.value;
  if (!area?.cidade && !area?.bairro && locationSearchQuery.value) {
    qFilter.value = locationSearchQuery.value;
  }
  applyFilters();
};

const handleLocationClear = () => {
  const removed = selectedLocationArea.value;
  cidadeFilter.value = '';
  bairroFilter.value = '';
  locationSearchQuery.value = '';
  page.value = 1;

  trackAnalyticsEvent(LAIS_TRACKING_EVENTS.LISTING_FILTER_REMOVED, {
    filter: 'location',
    source: 'cockpit_location',
    removed,
  });
  applyFilters();
};

const handleLocationSearchExpanded = () => {
  trackAnalyticsEvent(LAIS_TRACKING_EVENTS.LISTING_SEARCH_EXPANDED, {
    source: 'cockpit_location',
    filters: currentFilterPayload(),
  });
};

const allSelected = computed(
  () =>
    imoveis.value.length > 0 &&
    imoveis.value.every(i => selected.value.has(valueFrom(i, 'id')))
);
const toggleAll = () => {
  if (allSelected.value) {
    selected.value = new Set();
    return;
  }
  selected.value = new Set(imoveis.value.map(i => valueFrom(i, 'id')));
};
const toggleOne = id => {
  const nextSelected = new Set(selected.value);
  if (nextSelected.has(id)) nextSelected.delete(id);
  else nextSelected.add(id);
  selected.value = nextSelected;
};

const goToPreviousPage = () => {
  if (page.value <= 1) return;

  page.value -= 1;
  fetchImoveis();
};

const goToNextPage = () => {
  if (page.value >= totalPages.value) return;

  page.value += 1;
  fetchImoveis();
};

const baixarSelecionados = () => {
  const selectedImoveis = imoveis.value.filter(i =>
    selected.value.has(valueFrom(i, 'id'))
  );
  const rows = [['Título', 'Tipo', 'Cidade', 'Bairro', 'Valor', 'Status']];
  selectedImoveis.forEach(i => {
    const valor = i.finalidade === 'aluguel' ? i.valorAluguel : i.valorVenda;
    rows.push([
      i.titulo || '',
      i.tipo || '',
      i.cidade || '',
      i.bairro || '',
      valor || '',
      i.status || '',
    ]);
  });
  const csv = rows
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `imoveis-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const enviarSelecionados = () => {
  const selectedImoveis = imoveis.value.filter(i =>
    selected.value.has(valueFrom(i, 'id'))
  );
  const body = selectedImoveis
    .map(i => `• ${i.titulo} — ${i.cidade || ''}`)
    .join('\n');
  window.location.href = `mailto:?subject=${encodeURIComponent('Imóveis Imudei')}&body=${encodeURIComponent(body)}`;
};

const loadBulkFavoriteLists = async () => {
  if (!activeContactId.value) {
    bulkFavoriteLists.value = [];
    return false;
  }

  bulkFavoriteListsLoading.value = true;
  try {
    const { data } = await FavoriteListsAPI.list({
      contactId: activeContactId.value,
    });

    bulkFavoriteLists.value = (data.data || []).map(list => ({
      label: list.list_name,
      value: list.list_name,
    }));
    return true;
  } catch (error) {
    bulkFavoriteLists.value = [];
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível carregar as listas deste cliente.'
    );
    return false;
  } finally {
    bulkFavoriteListsLoading.value = false;
  }
};

const openBulkFavoriteDialog = async () => {
  if (!activeContactId.value) {
    useAlert('Abra a busca a partir de um cliente para salvar favoritos.');
    return;
  }

  if (!selectedFavoriteListingItems.value.length) {
    useAlert(
      selectedExternalCount.value
        ? 'Selecione imóveis nativos da conta para salvar como favoritos.'
        : 'Selecione ao menos um imóvel para adicionar aos favoritos.'
    );
    return;
  }

  const listsLoaded = await loadBulkFavoriteLists();
  if (!listsLoaded) return;

  showBulkFavoritesDialog.value = true;
};

const showBulkFavoriteResult = ({
  createdCount,
  duplicateCount,
  rejectedCount,
}) => {
  if (createdCount > 0 && duplicateCount > 0 && rejectedCount > 0) {
    useAlert(
      `${createdCount} imóveis adicionados. ${duplicateCount} duplicados foram ignorados e ${rejectedCount} não puderam ser salvos.`
    );
    return;
  }

  if (createdCount > 0 && duplicateCount > 0) {
    useAlert(
      `${createdCount} imóveis adicionados. ${duplicateCount} duplicados já estavam na lista e foram ignorados.`
    );
    return;
  }

  if (createdCount > 0 && rejectedCount > 0) {
    useAlert(
      `${createdCount} imóveis adicionados. ${rejectedCount} itens não puderam ser salvos.`
    );
    return;
  }

  if (createdCount > 0) {
    useAlert(
      `${createdCount} ${
        createdCount === 1 ? 'imóvel adicionado' : 'imóveis adicionados'
      } aos favoritos.`
    );
    return;
  }

  if (duplicateCount > 0 && rejectedCount > 0) {
    useAlert(
      `${duplicateCount} imóveis já estavam nesta lista e ${rejectedCount} itens não puderam ser salvos.`
    );
    return;
  }

  if (duplicateCount > 0) {
    useAlert('Todos os imóveis selecionados já estavam nesta lista.');
    return;
  }

  if (rejectedCount > 0) {
    useAlert(
      `${rejectedCount} itens selecionados não puderam ser salvos como favoritos.`
    );
    return;
  }

  useAlert('Nenhum imóvel foi adicionado aos favoritos.');
};

const submitBulkFavoriteAdd = async ({
  imovelIds,
  favoriteIds,
  targetListName,
}) => {
  if (!activeContactId.value || !targetListName) return;

  const ids = Array.from(
    new Set(
      (imovelIds || favoriteIds || [])
        .map(id => Number(id))
        .filter(id => Number.isInteger(id) && id > 0)
    )
  );

  if (!ids.length) {
    useAlert('Selecione ao menos um imóvel para adicionar aos favoritos.');
    return;
  }

  bulkFavoriteSaving.value = true;
  try {
    const { data } = await ContactFavoriteImoveisAPI.bulkCreate(
      activeContactId.value,
      {
        imovel_ids: ids,
        list_name: targetListName,
      }
    );
    const createdCount = Number(data?.meta?.count || data?.data?.length || 0);
    const skippedItems = data?.skipped || [];
    const skippedCount = Number(
      data?.meta?.skipped || skippedItems.length || 0
    );
    const duplicateCount = skippedItems.length
      ? skippedItems.filter(item => item?.reason === 'already_favorited').length
      : skippedCount;
    const rejectedCount = skippedItems.length
      ? Math.max(0, skippedCount - duplicateCount)
      : 0;

    showBulkFavoritesDialog.value = false;
    selected.value = new Set();
    await fetchLeadContext();
    trackAnalyticsEvent('favorites_bulk_added', {
      contact_id: activeContactId.value,
      list_name: targetListName,
      count: createdCount,
      skipped_count: skippedCount,
      duplicate_count: duplicateCount,
      rejected_count: rejectedCount,
      source: 'imoveis_index',
    });
    showBulkFavoriteResult({ createdCount, duplicateCount, rejectedCount });
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível adicionar os imóveis selecionados aos favoritos.'
    );
  } finally {
    bulkFavoriteSaving.value = false;
  }
};

const actions = computed(() =>
  [
    activeContactId.value && {
      capability: 'view',
      label: 'Adicionar aos favoritos',
      icon: 'i-lucide-heart-plus',
      disabled:
        selectedFavoriteListingItems.value.length === 0 ||
        bulkFavoriteSaving.value ||
        bulkFavoriteListsLoading.value,
      loading: bulkFavoriteSaving.value,
      onClick: openBulkFavoriteDialog,
    },
    {
      capability: 'bulk_export',
      label: 'Baixar selecionados',
      icon: 'i-lucide-download',
      disabled:
        selected.value.size === 0 || !capabilityFor('bulk_export').allowed,
      onClick: baixarSelecionados,
    },
    {
      capability: 'bulk_send',
      label: 'Enviar selecionados',
      icon: 'i-lucide-send',
      disabled:
        selected.value.size === 0 || !capabilityFor('bulk_send').allowed,
      onClick: enviarSelecionados,
    },
  ].filter(action => action && capabilityFor(action.capability).visible)
);

const refreshCatalog = () => fetchImoveis();

const publicUrlFor = imovel => publicUrlForListing(imovel);
const detailIdFor = imovel => detailIdForListing(imovel, catalogMode.value);

const cacheDetailItem = imovel => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(
    `lais_catalog_detail:${accountId.value}:${catalogMode.value}:${detailIdFor(imovel)}`,
    JSON.stringify(imovel)
  );
};

const setActiveListing = (card, source = 'list') => {
  if (!card?.id) return;
  activeListingId.value = card.id;

  if (source === 'carousel') {
    trackAnalyticsEvent(
      LAIS_TRACKING_EVENTS.LISTING_CAROUSEL_CHANGED,
      listingCardTrackingPayload(card, {
        sourceList: `imoveis_index:${catalogMode.value}:${viewMode.value}`,
      })
    );
  }
};

const openListingCard = card => {
  setActiveListing(card);
  trackAnalyticsEvent(
    LAIS_TRACKING_EVENTS.LISTING_CLICKED,
    listingCardTrackingPayload(card, {
      sourceList: `imoveis_index:${catalogMode.value}:${viewMode.value}`,
    })
  );
  cacheDetailItem(card.raw);
  router.push({
    name: 'imovel_catalog_show',
    params: {
      accountId: accountId.value,
      catalog: catalogMode.value,
      id: card.detailId,
    },
  });
};

const abrirLinkPublico = imovel => {
  const url = publicUrlFor(imovel);
  if (url) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  useAlert('Este imóvel ainda não possui link publicado.');
};

const openPublicLinkForCard = card => abrirLinkPublico(card.raw);
const toggleListingCard = card => toggleOne(card.id);

const sourceLabel = computed(() =>
  catalogMode.value === 'empreendimentos'
    ? 'Empreendimentos Imudei'
    : 'Base integrada de imóveis'
);
const selectedIdSet = computed(
  () => new Set(Array.from(selected.value).map(value => String(value)))
);
const selectedNativeImovelIds = computed(() =>
  Array.from(selected.value)
    .map(value => Number(value))
    .filter(value => Number.isInteger(value) && value > 0)
);
const selectedNativeImoveisOnPage = computed(() =>
  imoveis.value.filter(imovel => {
    const id = Number(valueFrom(imovel, 'id'));
    return (
      Number.isInteger(id) && id > 0 && selectedIdSet.value.has(String(id))
    );
  })
);
const isHighlightableImovel = imovel =>
  HIGHLIGHTABLE_STATUSES.includes(
    String(valueFrom(imovel, 'status') || '').toLowerCase()
  );
const selectedHighlightableImovelIds = computed(() =>
  selectedNativeImoveisOnPage.value
    .filter(isHighlightableImovel)
    .map(imovel => Number(valueFrom(imovel, 'id')))
);
const selectedBlockedByStatusCount = computed(() =>
  Math.max(
    0,
    selectedNativeImoveisOnPage.value.length -
      selectedHighlightableImovelIds.value.length
  )
);
const selectedExternalCount = computed(
  () => selected.value.size - selectedNativeImovelIds.value.length
);
const selectedFavoriteListingItems = computed(() =>
  listingCards.value
    .filter(card => {
      const id = Number(card.id);
      return (
        Number.isInteger(id) &&
        id > 0 &&
        selectedIdSet.value.has(String(card.id))
      );
    })
    .map(card => {
      const imovelId = Number(card.id);
      const raw = card.raw || {};

      return {
        id: imovelId,
        imovel_id: imovelId,
        favorited_at: '',
        imovel: {
          id: imovelId,
          titulo: card.title,
          bairro: valueFrom(raw, 'bairro', 'neighborhood'),
          cidade: valueFrom(raw, 'cidade', 'city'),
          tipo: card.typeLabel,
          finalidade:
            valueFrom(raw, 'finalidade') ||
            finalidadeFromBusinessContext(card.businessContext),
          status: valueFrom(raw, 'status') || card.status,
          valor_venda: valueFrom(
            raw,
            'valorVenda',
            'valor_venda',
            'salePrice',
            'sale_price'
          ),
          valor_aluguel: valueFrom(
            raw,
            'valorAluguel',
            'valor_aluguel',
            'rentPrice',
            'rent_price'
          ),
          thumbnail: card.coverPhoto,
        },
      };
    })
);
const importHistory = computed(() => importStatusPayload.value?.history || []);
const lastImport = computed(
  () =>
    importStatusPayload.value?.lastImport ||
    importStatusPayload.value?.last_import ||
    importHistory.value[0] ||
    null
);
const lastImportStatus = computed(() =>
  String(lastImport.value?.status || 'idle').toLowerCase()
);
const lastImportMeta = computed(
  () => IMPORT_STATUS_META[lastImportStatus.value] || IMPORT_STATUS_META.idle
);
const importStatusLabelFor = status => {
  switch (status) {
    case 'enqueued':
      return t('PROPERTIES.OPERATIONS.IMPORT.STATUS.ENQUEUED');
    case 'processing':
    case 'running':
      return t('PROPERTIES.OPERATIONS.IMPORT.STATUS.PROCESSING');
    case 'completed':
    case 'processed':
      return t('PROPERTIES.OPERATIONS.IMPORT.STATUS.COMPLETED');
    case 'failed':
      return t('PROPERTIES.OPERATIONS.IMPORT.STATUS.FAILED');
    case 'cancelled':
      return t('PROPERTIES.OPERATIONS.IMPORT.STATUS.CANCELLED');
    default:
      return t('PROPERTIES.OPERATIONS.IMPORT.STATUS.IDLE');
  }
};
const lastImportStatusLabel = computed(() =>
  importStatusLabelFor(lastImportStatus.value)
);
const lastImportTone = computed(() => lastImportMeta.value.tone);
const lastImportFinishedAt = computed(
  () =>
    lastImport.value?.completedAt ||
    lastImport.value?.completed_at ||
    lastImport.value?.cancelledAt ||
    lastImport.value?.cancelled_at ||
    lastImport.value?.startedAt ||
    lastImport.value?.started_at ||
    lastImport.value?.enqueuedAt ||
    lastImport.value?.enqueued_at ||
    ''
);
const lastImportDateLabel = computed(() =>
  formatDateTime(lastImportFinishedAt.value)
);
const lastImportCreated = computed(() => numericImportValue('created'));
const lastImportUpdated = computed(() => numericImportValue('updated'));
const lastImportFailed = computed(() => numericImportValue('failed'));
const lastImportSourceLabel = computed(
  () =>
    lastImport.value?.portal ||
    lastImport.value?.portalOrigem ||
    lastImport.value?.portal_origem ||
    t('PROPERTIES.OPERATIONS.IMPORT.UNKNOWN_SOURCE')
);
const lastImportErrorLabel = computed(() => {
  const errors = lastImport.value?.errors;
  if (Array.isArray(errors) && errors.length) return errors[0];
  return (
    lastImport.value?.errorMessage || lastImport.value?.error_message || ''
  );
});
const importRunPanelStatus = computed(() => {
  if (importLoading.value) return 'running';
  if (importError.value || lastImportErrorLabel.value) return 'failed';
  if (capabilitiesContext.value.state.stale) return 'stale';
  if (['processing', 'running'].includes(lastImportStatus.value)) {
    return 'running';
  }
  if (lastImportStatus.value === 'enqueued') return 'queued';
  if (['completed', 'processed'].includes(lastImportStatus.value)) {
    return 'completed';
  }
  if (lastImportStatus.value === 'cancelled') return 'cancelled';
  return 'queued';
});
const importRunNextSteps = computed(() => {
  if (importError.value || lastImportErrorLabel.value) {
    return [
      'Recarregue o status de importacao.',
      'Abra o console de importacao se a falha persistir.',
    ];
  }
  if (capabilitiesContext.value.state.stale) {
    return [
      'Atualize a listagem antes de programar destaques.',
      'Confirme o ultimo run no console de importacao.',
    ];
  }
  return [];
});
const importRunStatusPanel = computed(() => ({
  status: importRunPanelStatus.value,
  startedAt:
    lastImport.value?.startedAt ||
    lastImport.value?.started_at ||
    lastImport.value?.enqueuedAt ||
    lastImport.value?.enqueued_at ||
    '',
  finishedAt: lastImportFinishedAt.value,
  worker: 'imoveis_import_status',
  lock: {
    catalog: catalogMode.value,
    source: lastImportSourceLabel.value,
  },
  error: importError.value || lastImportErrorLabel.value,
  stale: capabilitiesContext.value.state.stale,
  progress: importLoading.value ? 45 : null,
  nextSteps: importRunNextSteps.value,
}));
const operationMetrics = computed(() => [
  {
    id: 'results',
    label: t('PROPERTIES.OPERATIONS.METRICS.RESULTS'),
    value: totalCount.value,
    helper: sourceLabel.value,
    icon: 'i-lucide-building-2',
    tone: 'info',
  },
  {
    id: 'selected',
    label: t('PROPERTIES.OPERATIONS.METRICS.SELECTED'),
    value: selected.value.size,
    helper: selectedExternalCount.value
      ? t('PROPERTIES.OPERATIONS.METRICS.SELECTED_WITH_EXTERNAL', {
          count: selectedExternalCount.value,
        })
      : t('PROPERTIES.OPERATIONS.METRICS.SELECTED_HELPER'),
    icon: 'i-lucide-check-square',
    tone: selected.value.size ? 'positive' : 'neutral',
  },
  {
    id: 'created',
    label: t('PROPERTIES.OPERATIONS.METRICS.CREATED'),
    value: lastImportCreated.value,
    helper: lastImportDateLabel.value,
    icon: 'i-lucide-upload-cloud',
    tone: lastImportCreated.value ? 'positive' : lastImportTone.value,
  },
  {
    id: 'failed',
    label: t('PROPERTIES.OPERATIONS.METRICS.FAILED'),
    value: lastImportFailed.value,
    helper: lastImportStatusLabel.value,
    icon: 'i-lucide-triangle-alert',
    tone: lastImportFailed.value ? 'danger' : 'neutral',
  },
]);
// Presentation-only summary tiles for the top of the page (hybrid layout).
// Derived from state already computed above; no data fetching or logic.
const finalidadeSummaryLabel = computed(
  () =>
    ({
      todos: 'Todas as finalidades',
      venda: 'À venda',
      aluguel: 'Para alugar',
      temporada: 'Temporada',
    })[finalidadeFilter.value] || 'Todas as finalidades'
);
const summaryMetricItems = computed(() => [
  {
    id: 'total',
    label:
      catalogMode.value === 'empreendimentos' ? 'Empreendimentos' : 'Imóveis',
    value: totalCount.value,
    helper: sourceLabel.value,
    icon:
      catalogMode.value === 'empreendimentos'
        ? 'i-lucide-building-2'
        : 'i-lucide-home',
    tone: 'info',
  },
  {
    id: 'finalidade',
    label: 'Finalidade',
    value: finalidadeSummaryLabel.value,
    helper: hasActiveFilters.value
      ? 'Filtros aplicados nesta busca'
      : 'Nenhum filtro aplicado',
    icon: 'i-lucide-target',
    tone: 'neutral',
  },
  {
    id: 'selected',
    label: 'Selecionados',
    value: selected.value.size,
    helper: selected.value.size
      ? 'Prontos para exportar, enviar ou favoritar'
      : 'Selecione imóveis para agir em lote',
    icon: 'i-lucide-check-square',
    tone: selected.value.size ? 'positive' : 'neutral',
  },
]);
const canScheduleBulkHighlight = computed(() => {
  if (
    !canManageBulkHighlightAction.value ||
    selectedHighlightableImovelIds.value.length === 0
  ) {
    return false;
  }

  if (!highlightForm.scheduledFor) return false;
  const date = new Date(highlightForm.scheduledFor);
  return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
});
const highlightPlacementLabel = value => {
  switch (value) {
    case 'vitrine':
      return t('PROPERTIES.HIGHLIGHT.PLACEMENTS.VITRINE');
    case 'destaque':
      return t('PROPERTIES.HIGHLIGHT.PLACEMENTS.DESTAQUE');
    case 'super_destaque':
      return t('PROPERTIES.HIGHLIGHT.PLACEMENTS.SUPER');
    default:
      return value;
  }
};
const highlightScheduledPreview = computed(() =>
  highlightForm.scheduledFor ? formatDateTime(highlightForm.scheduledFor) : ''
);
const bulkHighlightHelper = computed(() => {
  const capability = capabilityFor('bulk_highlight');
  if (!canManageBulkHighlight.value) {
    return t('PROPERTIES.OPERATIONS.HIGHLIGHT.PERMISSION');
  }
  if (capability.deniedBy === 'stale') {
    return 'Atualize os dados antes de programar novos destaques.';
  }
  if (capability.deniedBy === 'partial') {
    return 'A busca retornou dados parciais; programe destaques após uma atualização completa.';
  }
  if (selectedNativeImovelIds.value.length === 0) {
    return t('PROPERTIES.OPERATIONS.HIGHLIGHT.SELECT_HELPER');
  }
  if (selectedHighlightableImovelIds.value.length === 0) {
    return 'Destaque usa apenas imóveis disponíveis; ajuste a seleção ou filtre por disponíveis.';
  }
  return t('PROPERTIES.OPERATIONS.HIGHLIGHT.READY_HELPER', {
    count: selectedHighlightableImovelIds.value.length,
  });
});

const numericImportValue = key => {
  const value = Number(lastImport.value?.[key]);
  return Number.isFinite(value) ? value : 0;
};

const formatDateTime = value => {
  if (!value) return t('PROPERTIES.OPERATIONS.IMPORT.NO_DATE');

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return t('PROPERTIES.OPERATIONS.IMPORT.NO_DATE');
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const fetchImportStatus = async () => {
  if (!canOpenImportConsole.value) return;

  importLoading.value = true;
  importError.value = '';
  try {
    const { data } = await ImovelAPI.importStatus();
    importStatusPayload.value = camelcaseKeys(data || {}, { deep: true });
  } catch {
    importError.value = t('PROPERTIES.OPERATIONS.IMPORT.LOAD_ERROR');
  } finally {
    importLoading.value = false;
  }
};

const openCreateProperty = () => {
  if (!canCreateImovelAction.value) return;

  router.push({
    name: 'lais_criar_imovel_index',
    params: { accountId: accountId.value },
  });
};

const openImportConsole = () => {
  if (!canOpenImportConsoleAction.value) return;

  router.push({
    name: 'imoveis_import_operations',
    params: { accountId: accountId.value },
  });
};

const openDuplicateCandidates = () => {
  if (!canReviewDuplicatesAction.value) return;

  router.push({
    name: 'imoveis_duplicate_candidates',
    params: { accountId: accountId.value },
  });
};

const openPortalSyncOperations = () => {
  if (!canReviewPortalSyncAction.value) return;

  router.push({
    name: 'imoveis_portal_sync_operations',
    params: { accountId: accountId.value },
  });
};

const openNewImport = () => {
  if (!canStartImportAction.value) return;

  router.push({
    name: 'lais_criar_imovel_index',
    params: { accountId: accountId.value },
    query: { modo: 'importacao', origem: 'imoveis_index' },
  });
};

const setOperationMode = mode => {
  if (!OPERATION_MODE_VALUES.includes(mode)) return;

  router
    .replace({
      path: route.path,
      query: compactQuery({
        ...(route.query || {}),
        operacao: mode === DEFAULT_OPERATION_MODE ? undefined : mode,
      }),
    })
    .catch(() => {});
};

const openPublicationQueue = () => {
  if (!canReviewPublicationQueueAction.value) return;

  statusFilter.value = IMOVEL_STATUS.DISPONIVEL;
  apenasDisponiveis.value = true;
  page.value = 1;
  applyFilters({ operationMode: 'publicacao' });
};

const focusBulkHighlight = () => {
  nextTick(() => {
    document.getElementById('bulk-highlight-scheduled-for')?.focus();
  });
};

const submitBulkHighlight = async () => {
  highlightError.value = '';

  if (!canManageBulkHighlight.value) {
    highlightError.value = t('PROPERTIES.OPERATIONS.HIGHLIGHT.PERMISSION');
    return;
  }

  if (!canManageBulkHighlightAction.value) {
    highlightError.value = bulkHighlightHelper.value;
    return;
  }

  if (!selectedNativeImovelIds.value.length) {
    highlightError.value = t('PROPERTIES.OPERATIONS.HIGHLIGHT.EMPTY_ERROR');
    return;
  }

  if (!selectedHighlightableImovelIds.value.length) {
    highlightError.value =
      'Selecione ao menos um imóvel disponível para programar destaque.';
    return;
  }

  if (!canScheduleBulkHighlight.value) {
    highlightError.value = t('PROPERTIES.HIGHLIGHT.INVALID_DATE');
    return;
  }

  highlightSubmitting.value = true;
  try {
    const { data } = await ImovelAPI.programarDestaque({
      imovel_ids: selectedHighlightableImovelIds.value,
      scheduled_for: new Date(highlightForm.scheduledFor).toISOString(),
      placement: highlightForm.placement,
      notes: highlightForm.notes.trim(),
    });
    const scheduledCount =
      data?.scheduled || selectedHighlightableImovelIds.value.length;
    selected.value = new Set();
    highlightForm.scheduledFor = '';
    highlightForm.placement = 'vitrine';
    highlightForm.notes = '';
    await refreshCatalog();
    useAlert(
      data?.provider_status === 'awaiting_provider_configuration'
        ? t('PROPERTIES.HIGHLIGHT.SUCCESS.AWAITING')
        : t('PROPERTIES.OPERATIONS.HIGHLIGHT.SUCCESS', {
            count: scheduledCount,
          })
    );
  } catch (error) {
    highlightError.value =
      error?.response?.data?.errors?.[0] ||
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      t('PROPERTIES.HIGHLIGHT.ERROR');
    useAlert(highlightError.value);
  } finally {
    highlightSubmitting.value = false;
  }
};

const scrollListingResultsToTop = () => {
  nextTick(() => {
    if (!listingResultsEl.value) return;
    listingResultsEl.value.scrollTop = 0;
  });
};

const setViewMode = mode => {
  if (!VIEW_MODES.includes(mode) || viewMode.value === mode) return;
  const leavingMap = viewMode.value === 'map' && mode !== 'map';
  viewMode.value = mode;
  if (leavingMap) {
    activeListingId.value = '';
    scrollListingResultsToTop();
  }
  syncRouteState();
};

const viewedCardKey = card =>
  `${catalogMode.value}:${viewMode.value}:${card.id}:${card.position}`;

const trackListingCardViewed = card => {
  const key = viewedCardKey(card);
  if (viewedCardKeys.value.has(key)) return;

  const nextViewed = new Set(viewedCardKeys.value);
  nextViewed.add(key);
  viewedCardKeys.value = nextViewed;
  trackAnalyticsEvent(
    LAIS_TRACKING_EVENTS.LISTING_VIEWED,
    listingCardTrackingPayload(card, {
      sourceList: `imoveis_index:${catalogMode.value}:${viewMode.value}`,
    })
  );
};

const handleCardShare = (card, payload) => {
  trackAnalyticsEvent(
    LAIS_TRACKING_EVENTS.LISTING_SHARE_CLICKED,
    listingCardTrackingPayload(card, {
      sourceList: `imoveis_index:${catalogMode.value}:${viewMode.value}`,
      shareMethod: payload.method,
    })
  );
  useAlert('Link do imóvel pronto para compartilhar.');
};

const handleCardShareError = () => {
  useAlert('Não foi possível compartilhar este imóvel.');
};

const openAlertsCenterFromDiscovery = () => {
  trackAnalyticsEvent(LAIS_TRACKING_EVENTS.LISTING_ALERT_CENTER_CLICKED, {
    sourceList: `imoveis_index:${catalogMode.value}:${viewMode.value}`,
    totalResults: totalCount.value,
    filters: currentFilterPayload(),
  });

  router.push({
    name: 'notifications_index',
    params: { accountId: accountId.value },
    query: compactQuery({
      tab: 'alertas',
      source: 'imoveis_index',
      catalog: catalogMode.value,
      finalidade:
        finalidadeFilter.value === 'todos' ? undefined : finalidadeFilter.value,
      cidade: cidadeFilter.value.trim(),
      bairro: bairroFilter.value.trim(),
      tipo: tipoFilter.value.trim(),
      quartos_min: quartosMinFilter.value,
    }),
  });
};

watch(
  canViewImoveis,
  allowed => {
    if (!allowed || hasLoadedCatalogAfterPermission.value) return;

    hasLoadedCatalogAfterPermission.value = true;
    fetchImoveis();
    fetchImportStatus();
  },
  { immediate: true }
);
watch(
  () => route.query.contact_id,
  () => {
    fetchLeadContext();
  },
  { immediate: true }
);
watch(
  () => [
    route.path,
    route.query.catalog,
    route.query.q,
    route.query.finalidade,
    route.query.cidade,
    route.query.bairro,
    route.query.tipo,
    route.query.quartos_min,
    route.query.portal_origem,
    route.query.status,
    route.query.apenas_disponiveis,
    route.query.view,
    route.query.page,
    route.query.per_page,
  ],
  async () => {
    if (isSyncingRouteState) return;
    applyRouteState();
    await fetchImoveis({ syncRoute: false });
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisOperationalPage
    :title="pageTitle"
    :helper="headerHelper"
    :loading="isLoading && !hasListings"
  >
    <template #actions>
      <template v-if="canViewImoveis">
        <LaisFavoritesBadge
          v-if="activeContactId"
          :count="favoritesCount"
          label="Favoritos do cliente"
          interactive
          @click="openFavoritesForLead"
        />
        <LaisPrimaryButton
          v-if="canCreateImovel"
          icon="i-lucide-plus"
          :disabled="!canCreateImovelAction"
          @click="openCreateProperty"
        >
          Novo imóvel
        </LaisPrimaryButton>
        <LaisFilterButton
          v-if="canUseImoveisFilters"
          :active="hasActiveFilters"
          @click="showFilters = !showFilters"
        />
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          :is-loading="isLoading"
          @click="refreshCatalog"
        >
          Atualizar
        </LaisPrimaryButton>
      </template>
    </template>

    <template #metrics>
      <LaisMetricStrip
        v-if="canViewImoveis"
        :items="summaryMetricItems"
        aria-label="Resumo dos imóveis"
      />
    </template>

    <template #filters>
      <div v-if="canViewImoveis" class="flex flex-col gap-3">
        <div class="flex min-w-0 flex-wrap gap-2">
          <button
            v-for="option in catalogOptions"
            :key="option.value"
            type="button"
            class="inline-flex min-w-[150px] items-center gap-2 rounded-lais border px-3 py-2 text-left transition-colors"
            :class="
              catalogMode === option.value
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text hover:border-lais-primary hover:bg-lais-primary-50/40'
            "
            @click="setCatalogMode(option.value)"
          >
            <span
              class="size-4 shrink-0"
              :class="option.icon"
              aria-hidden="true"
            />
            <span class="min-w-0">
              <span class="block text-[13px] font-semibold leading-tight">
                {{ option.label }}
              </span>
              <span class="block truncate text-[11px] opacity-75">
                {{ option.helper }}
              </span>
            </span>
          </button>
        </div>
        <div class="flex min-w-0 flex-wrap gap-2">
          <button
            v-for="tab in businessContextTabs"
            :key="tab.value"
            type="button"
            class="inline-flex min-w-[130px] items-center justify-between gap-3 rounded-lais-sm border px-3 py-2 text-left transition-colors"
            :class="
              finalidadeFilter === tab.value
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text hover:border-lais-primary hover:bg-lais-primary-50/40'
            "
            @click="setBusinessContext(tab.value)"
          >
            <span class="min-w-0">
              <span class="block text-[13px] font-semibold leading-tight">
                {{ tab.label }}
              </span>
              <span class="block truncate text-[11px] opacity-75">
                {{ tab.helper }}
              </span>
            </span>
          </button>
        </div>
      </div>

      <div
        v-if="canViewImoveis && canUseImoveisFilters"
        class="-mx-4 sm:-mx-6 lg:-mx-10"
      >
        <LaisFilterBar :show="showFilters">
          <div class="flex min-w-[220px] flex-[1.4] flex-col gap-1">
            <label
              class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
            >
              Busca
            </label>
            <input
              v-model="qFilter"
              type="search"
              placeholder="Código, rua, bairro, cidade ou endereço completo"
              class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
              @keyup.enter="applyFilters"
              @blur="applyFilters"
            />
          </div>
          <div class="min-w-[240px] flex-[1.2]">
            <LaisCockpitLocation
              v-model:query="locationSearchQuery"
              :model-value="selectedLocationArea"
              :recent-searches="recentSearches"
              :contact-id="activeContactId"
              @select="handleLocationSelect"
              @select-recent="applyRecentSearch"
              @clear="handleLocationClear"
              @search-expanded="handleLocationSearchExpanded"
            />
          </div>
          <div
            class="flex min-w-[150px] flex-1 flex-col gap-1 sm:max-w-[170px]"
          >
            <label
              class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
            >
              Finalidade
            </label>
            <select
              v-model="finalidadeFilter"
              class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text focus:outline-none focus:border-lais-primary"
              @change="applyFilters"
            >
              <option value="todos">Todos</option>
              <option value="venda">Venda</option>
              <option value="aluguel">Aluguel</option>
              <option value="temporada">Temporada</option>
            </select>
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-[180px]">
            <label
              class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
            >
              Cidade
            </label>
            <input
              v-model="cidadeFilter"
              type="text"
              placeholder="Buscar por cidade"
              class="h-10 px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
              @keyup.enter="applyFilters"
              @blur="applyFilters"
            />
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-[180px]">
            <label
              class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
            >
              Bairro
            </label>
            <input
              v-model="bairroFilter"
              type="text"
              placeholder="Buscar por bairro"
              class="h-10 px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
              @keyup.enter="applyFilters"
              @blur="applyFilters"
            />
          </div>
          <div
            class="flex min-w-[150px] flex-1 flex-col gap-1 sm:max-w-[170px]"
          >
            <label
              class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
            >
              Quartos
            </label>
            <input
              v-model="quartosMinFilter"
              min="0"
              type="number"
              placeholder="Mín."
              class="h-10 px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
              @keyup.enter="applyFilters"
              @blur="applyFilters"
            />
          </div>
          <div
            class="flex min-w-[170px] flex-1 flex-col gap-1 sm:max-w-[200px]"
          >
            <label
              class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
            >
              Fonte
            </label>
            <input
              v-model="portalOrigemFilter"
              type="text"
              :placeholder="
                catalogMode === 'empreendimentos' ? 'Fornecedor' : 'Origem'
              "
              class="h-10 px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
              @keyup.enter="applyFilters"
              @blur="applyFilters"
            />
          </div>
          <div
            class="flex min-w-[160px] flex-1 flex-col gap-1 sm:max-w-[190px]"
          >
            <label
              class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
            >
              Status
            </label>
            <select
              v-model="statusFilter"
              class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text focus:outline-none focus:border-lais-primary"
              @change="applyFilters"
            >
              <option value="">Todos</option>
              <option
                v-for="option in statusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <label
            class="flex items-center gap-2 h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] text-lais-text cursor-pointer"
          >
            <input
              v-model="apenasDisponiveis"
              type="checkbox"
              class="size-4 accent-lais-primary rounded"
              @change="applyFilters"
            />
            Apenas disponíveis
          </label>
          <button
            type="button"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
            @click="clearFilters"
          >
            Limpar
          </button>
        </LaisFilterBar>
      </div>
    </template>

    <div v-if="!canViewImoveis">
      <LaisPermissionState
        data-test="imoveis-permission-state"
        variant="denied"
        title="Acesso restrito aos imóveis"
        description="Seu perfil não tem permissão para ver ou operar imóveis nesta conta. Fale com um administrador para liberar o acesso. Nenhuma busca é disparada antes deste acesso."
      />
    </div>

    <div v-else class="flex flex-col gap-5">
      <details
        v-if="hasOperationalActions"
        class="group rounded-lais border border-lais-border-soft bg-lais-bg-soft"
        :aria-label="t('PROPERTIES.OPERATIONS.ARIA_LABEL')"
      >
        <summary
          class="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 sm:px-5"
        >
          <span class="flex min-w-0 items-center gap-2">
            <span
              class="i-lucide-settings-2 size-4 shrink-0 text-lais-text-muted"
              aria-hidden="true"
            />
            <span class="min-w-0">
              <span class="block text-[13px] font-semibold text-lais-text">
                Ferramentas de operação
              </span>
              <span class="block truncate text-[12px] text-lais-text-muted">
                Importação, publicação e destaque — ver detalhes técnicos
              </span>
            </span>
          </span>
          <span
            class="i-lucide-chevron-down size-4 shrink-0 text-lais-text-muted transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>

        <div
          class="flex flex-col gap-4 border-t border-lais-border-soft px-4 py-4 sm:px-5"
        >
          <div
            class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
          >
            <div class="min-w-0">
              <p
                class="font-redHatDisplay text-[18px] font-semibold leading-tight text-lais-text"
              >
                {{ t('PROPERTIES.OPERATIONS.TITLE') }}
              </p>
              <p class="mt-1 max-w-3xl text-[13px] text-lais-text-muted">
                {{ t('PROPERTIES.OPERATIONS.HELPER') }}
              </p>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <LaisPrimaryButton
                v-if="canCreateImovel"
                variant="ghost"
                size="sm"
                icon="i-lucide-plus"
                :disabled="!canCreateImovelAction"
                @click="openCreateProperty"
              >
                {{ t('PROPERTIES.OPERATIONS.CREATE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canOpenImportConsole"
                variant="ghost"
                size="sm"
                icon="i-lucide-upload"
                :disabled="!canOpenImportConsoleAction"
                @click="openImportConsole"
              >
                {{ t('PROPERTIES.OPERATIONS.IMPORT.OPEN') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canReviewDuplicates"
                variant="ghost"
                size="sm"
                icon="i-lucide-git-merge"
                :disabled="!canReviewDuplicatesAction"
                @click="openDuplicateCandidates"
              >
                Duplicidades
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canReviewPortalSync"
                variant="ghost"
                size="sm"
                icon="i-lucide-globe"
                :disabled="!canReviewPortalSyncAction"
                @click="openPortalSyncOperations"
              >
                Portais
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canReviewPublicationQueue"
                variant="ghost"
                size="sm"
                icon="i-lucide-radio-tower"
                :disabled="!canReviewPublicationQueueAction"
                @click="openPublicationQueue"
              >
                {{ t('PROPERTIES.OPERATIONS.PUBLICATION.REVIEW_QUEUE') }}
              </LaisPrimaryButton>
            </div>
          </div>

          <div
            class="grid gap-2 md:grid-cols-3"
            data-test="imoveis-operation-tabs"
          >
            <button
              v-for="mode in visibleOperationModes"
              :key="mode.value"
              type="button"
              class="flex min-h-[74px] items-start gap-3 rounded-lais border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-55"
              :class="
                activeOperationMode === mode.value
                  ? 'border-lais-primary bg-white text-lais-primary shadow-lais-card'
                  : 'border-lais-border bg-white/70 text-lais-text hover:border-lais-primary hover:bg-white'
              "
              :data-test="`imoveis-operation-tab-${mode.value}`"
              :data-state="mode.deniedBy || 'ready'"
              :aria-pressed="activeOperationMode === mode.value"
              :disabled="mode.disabled"
              @click="setOperationMode(mode.value)"
            >
              <span
                class="mt-0.5 size-4 shrink-0"
                :class="mode.icon"
                aria-hidden="true"
              />
              <span class="min-w-0">
                <span class="block text-[13px] font-semibold leading-tight">
                  {{ mode.label }}
                </span>
                <span
                  class="mt-1 block text-[12px] leading-snug text-lais-text-muted"
                >
                  {{ mode.helper }}
                </span>
              </span>
            </button>
          </div>

          <div
            v-if="capabilityNotice"
            class="rounded-lais-sm border px-4 py-3 text-[13px] leading-relaxed"
            data-test="imoveis-partial-stale-state"
            :data-state="capabilitiesContext.state.status"
            :class="
              capabilityNotice.tone === 'danger'
                ? 'border-lais-danger/40 bg-lais-danger-50 text-lais-danger'
                : 'border-lais-warning/40 bg-lais-warning/10 text-lais-text-body'
            "
          >
            <p class="font-semibold text-lais-text">
              {{ capabilityNotice.title }}
            </p>
            <p class="mt-1">{{ capabilityNotice.description }}</p>
          </div>

          <LaisMetricStrip
            :items="operationMetrics"
            :loading="importLoading && !lastImport"
            :aria-label="t('PROPERTIES.OPERATIONS.METRICS.ARIA_LABEL')"
          />

          <div class="grid gap-4">
            <LaisCard
              v-if="activeOperationMode === 'importacao'"
              data-test="imoveis-import-console-panel"
              :title="t('PROPERTIES.OPERATIONS.IMPORT.TITLE')"
              density="cozy"
            >
              <LaisRunStatusPanel
                class="mb-4"
                data-test="imoveis-import-run-status"
                v-bind="importRunStatusPanel"
              />
              <div class="flex flex-col gap-4 md:flex-row md:items-start">
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-2">
                    <LaisStatusBadge
                      :label="lastImportStatusLabel"
                      :tone="lastImportTone"
                      dot
                    />
                    <span class="text-[12px] text-lais-text-muted">
                      {{ lastImportSourceLabel }}
                    </span>
                  </div>
                  <dl class="mt-3 grid gap-2 text-[13px] sm:grid-cols-3">
                    <div>
                      <dt class="text-lais-text-muted">
                        {{ t('PROPERTIES.OPERATIONS.IMPORT.CREATED') }}
                      </dt>
                      <dd class="font-semibold text-lais-text tabular-nums">
                        {{ lastImportCreated }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-lais-text-muted">
                        {{ t('PROPERTIES.OPERATIONS.IMPORT.UPDATED') }}
                      </dt>
                      <dd class="font-semibold text-lais-text tabular-nums">
                        {{ lastImportUpdated }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-lais-text-muted">
                        {{ t('PROPERTIES.OPERATIONS.IMPORT.FAILED') }}
                      </dt>
                      <dd
                        class="font-semibold tabular-nums"
                        :class="
                          lastImportFailed
                            ? 'text-lais-danger'
                            : 'text-lais-text'
                        "
                      >
                        {{ lastImportFailed }}
                      </dd>
                    </div>
                  </dl>
                  <p class="mt-3 text-[12px] text-lais-text-muted">
                    {{
                      t('PROPERTIES.OPERATIONS.IMPORT.LAST_RUN', {
                        date: lastImportDateLabel,
                      })
                    }}
                  </p>
                  <p
                    v-if="importError || lastImportErrorLabel"
                    class="mt-3 rounded-lais-sm border border-lais-danger/40 bg-lais-danger-50 px-3 py-2 text-[12px] leading-relaxed text-lais-danger"
                  >
                    {{ importError || lastImportErrorLabel }}
                  </p>
                </div>
                <div class="flex shrink-0 flex-wrap gap-2 md:flex-col">
                  <LaisPrimaryButton
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-refresh-cw"
                    :is-loading="importLoading"
                    :disabled="!canOpenImportConsoleAction"
                    @click="fetchImportStatus"
                  >
                    {{ t('PROPERTIES.OPERATIONS.IMPORT.REFRESH') }}
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    v-if="canStartImport"
                    variant="primary"
                    size="sm"
                    icon="i-lucide-file-up"
                    :disabled="!canStartImportAction"
                    @click="openNewImport"
                  >
                    {{ t('PROPERTIES.OPERATIONS.IMPORT.START') }}
                  </LaisPrimaryButton>
                </div>
              </div>
            </LaisCard>

            <LaisCard
              v-if="activeOperationMode === 'publicacao'"
              data-test="imoveis-publication-console-panel"
              title="Publicação e portais"
              density="cozy"
            >
              <div class="grid gap-4">
                <p
                  class="max-w-3xl text-[13px] leading-relaxed text-lais-text-body"
                >
                  Revise imóveis prontos para publicar, o estado de cada portal
                  e possíveis duplicidades sem perder os filtros atuais da
                  listagem.
                </p>
                <div class="grid gap-3 md:grid-cols-3">
                  <LaisPrimaryButton
                    data-test="imoveis-publication-review-available"
                    variant="primary"
                    size="sm"
                    icon="i-lucide-radio-tower"
                    :disabled="!canReviewPublicationQueueAction"
                    @click="openPublicationQueue"
                  >
                    {{ t('PROPERTIES.OPERATIONS.PUBLICATION.REVIEW_QUEUE') }}
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    v-if="canReviewPortalSync"
                    data-test="imoveis-publication-portal-console"
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-globe"
                    :disabled="!canReviewPortalSyncAction"
                    @click="openPortalSyncOperations"
                  >
                    Console de portais
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    v-if="canReviewDuplicates"
                    data-test="imoveis-publication-duplicates-console"
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-git-merge"
                    :disabled="!canReviewDuplicatesAction"
                    @click="openDuplicateCandidates"
                  >
                    Duplicidades
                  </LaisPrimaryButton>
                </div>
                <dl class="grid gap-3 text-[13px] sm:grid-cols-3">
                  <div
                    class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2"
                  >
                    <dt class="text-[12px] text-lais-text-muted">
                      Status aplicado
                    </dt>
                    <dd class="mt-1 font-semibold text-lais-text">
                      {{ IMOVEL_STATUS_LABELS[IMOVEL_STATUS.DISPONIVEL] }}
                    </dd>
                  </div>
                  <div
                    class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2"
                  >
                    <dt class="text-[12px] text-lais-text-muted">
                      Finalidade preservada
                    </dt>
                    <dd class="mt-1 font-semibold text-lais-text">
                      {{
                        finalidadeFilter === 'todos'
                          ? 'Todos'
                          : finalidadeFilter
                      }}
                    </dd>
                  </div>
                  <div
                    class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2"
                  >
                    <dt class="text-[12px] text-lais-text-muted">
                      Filtro de disponibilidade
                    </dt>
                    <dd class="mt-1 font-semibold text-lais-text">
                      {{
                        apenasDisponiveis ? 'Ativo' : 'Será ativado no botão'
                      }}
                    </dd>
                  </div>
                </dl>
              </div>
            </LaisCard>

            <LaisCard
              v-if="activeOperationMode === 'destaque_programado'"
              data-test="imoveis-highlight-schedule-panel"
              :title="t('PROPERTIES.OPERATIONS.HIGHLIGHT.TITLE')"
              density="cozy"
            >
              <form class="grid gap-3" @submit.prevent="submitBulkHighlight">
                <p class="text-[13px] leading-relaxed text-lais-text-body">
                  {{ bulkHighlightHelper }}
                </p>
                <div
                  v-if="selectedExternalCount"
                  class="rounded-lais-sm border border-lais-warning/40 bg-lais-warning/10 px-3 py-2 text-[12px] leading-relaxed text-lais-text-body"
                >
                  {{
                    t('PROPERTIES.OPERATIONS.HIGHLIGHT.EXTERNAL_IGNORED', {
                      count: selectedExternalCount,
                    })
                  }}
                </div>
                <div
                  v-if="selectedBlockedByStatusCount"
                  data-test="imoveis-highlight-status-blocked"
                  class="rounded-lais-sm border border-lais-warning/40 bg-lais-warning/10 px-3 py-2 text-[12px] leading-relaxed text-lais-text-body"
                >
                  Destaque usa apenas imóveis disponíveis.
                  {{ selectedBlockedByStatusCount }} selecionados ficam
                  bloqueados pelo status atual.
                </div>
                <div class="grid gap-3 sm:grid-cols-2">
                  <div class="grid gap-1.5">
                    <label
                      for="bulk-highlight-scheduled-for"
                      class="text-[12px] font-semibold text-lais-text"
                    >
                      {{ t('PROPERTIES.HIGHLIGHT.DATE_LABEL') }}
                    </label>
                    <input
                      id="bulk-highlight-scheduled-for"
                      v-model="highlightForm.scheduledFor"
                      type="datetime-local"
                      :disabled="
                        highlightSubmitting || !canManageBulkHighlightAction
                      "
                      class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                    />
                  </div>
                  <div class="grid gap-1.5">
                    <label
                      for="bulk-highlight-placement"
                      class="text-[12px] font-semibold text-lais-text"
                    >
                      {{ t('PROPERTIES.HIGHLIGHT.PLACEMENT_LABEL') }}
                    </label>
                    <select
                      id="bulk-highlight-placement"
                      v-model="highlightForm.placement"
                      :disabled="
                        highlightSubmitting || !canManageBulkHighlightAction
                      "
                      class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
                    >
                      <option
                        v-for="option in HIGHLIGHT_PLACEMENTS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ highlightPlacementLabel(option.value) }}
                      </option>
                    </select>
                  </div>
                </div>
                <label
                  for="bulk-highlight-notes"
                  class="text-[12px] font-semibold text-lais-text"
                >
                  {{ t('PROPERTIES.HIGHLIGHT.NOTES_LABEL') }}
                </label>
                <textarea
                  id="bulk-highlight-notes"
                  v-model="highlightForm.notes"
                  rows="2"
                  maxlength="280"
                  :disabled="
                    highlightSubmitting || !canManageBulkHighlightAction
                  "
                  :placeholder="t('PROPERTIES.HIGHLIGHT.NOTES_PLACEHOLDER')"
                  class="w-full rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[13px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
                />
                <p
                  v-if="highlightScheduledPreview"
                  class="text-[12px] text-lais-text-muted"
                >
                  {{
                    t('PROPERTIES.OPERATIONS.HIGHLIGHT.PREVIEW', {
                      date: highlightScheduledPreview,
                      placement: highlightPlacementLabel(
                        highlightForm.placement
                      ),
                    })
                  }}
                </p>
                <p
                  v-if="highlightError"
                  class="rounded-lais-sm border border-lais-danger/40 bg-lais-danger-50 px-3 py-2 text-[12px] leading-relaxed text-lais-danger"
                >
                  {{ highlightError }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <LaisPrimaryButton
                    type="submit"
                    size="sm"
                    icon="i-lucide-sparkles"
                    :disabled="!canScheduleBulkHighlight || highlightSubmitting"
                    :is-loading="highlightSubmitting"
                  >
                    {{ t('PROPERTIES.OPERATIONS.HIGHLIGHT.SUBMIT') }}
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-mouse-pointer-2"
                    @click="focusBulkHighlight"
                  >
                    {{ t('PROPERTIES.OPERATIONS.HIGHLIGHT.FOCUS') }}
                  </LaisPrimaryButton>
                </div>
              </form>
            </LaisCard>
          </div>
        </div>
      </details>

      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="text-[13px] text-lais-text-body">
          <span class="font-semibold text-lais-text">{{ totalCount }}</span>
          {{ resultKindLabel }}
          <span class="text-lais-text-muted">
            ·
            {{
              hasActiveFilters
                ? t('PROPERTIES.LIST.SUMMARY_FILTERED')
                : t('PROPERTIES.LIST.SUMMARY_TOTAL')
            }}
          </span>
        </p>
        <span
          class="rounded-full bg-lais-primary-50 px-3 py-1 text-[12px] font-semibold text-lais-primary"
        >
          {{ sourceLabel }}
        </span>
      </div>

      <LaisRecentSearches
        v-if="activeContactId"
        :items="recentSearches"
        :active-slug="activeFilterSlug"
        show-remove
        @remove="removeRecentSearch"
        @select="applyRecentSearch"
      />

      <div ref="listingResultsEl" class="min-w-0">
        <div
          class="mb-3 hidden items-center justify-between gap-3 rounded-lais border border-lais-border-soft bg-white px-3 py-2 text-[13px] text-lais-text md:flex"
        >
          <div class="flex flex-wrap items-center gap-2">
            <label class="inline-flex items-center gap-2">
              <input
                type="checkbox"
                :checked="allSelected"
                class="size-4 accent-lais-primary"
                @change="toggleAll"
              />
              {{ t('PROPERTIES.LIST.SELECT_PAGE') }}
            </label>
            <button
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-border bg-white px-3 font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
              @click="openAlertsCenterFromDiscovery"
            >
              <span class="i-lucide-bell-plus size-4" aria-hidden="true" />
              {{ t('PROPERTIES.LIST.OPEN_ALERTS') }}
            </button>
          </div>
          <div
            class="inline-flex rounded-lais border border-lais-border bg-lais-bg-soft p-1"
          >
            <button
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm px-3 font-semibold transition-colors"
              :class="
                viewMode === 'list'
                  ? 'bg-white text-lais-primary shadow-lais-card'
                  : 'text-lais-text-muted hover:text-lais-text'
              "
              @click="setViewMode('list')"
            >
              <span class="i-lucide-list size-4" aria-hidden="true" />
              {{ t('PROPERTIES.LIST.VIEW.LIST') }}
            </button>
            <button
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm px-3 font-semibold transition-colors"
              :class="
                viewMode === 'side'
                  ? 'bg-white text-lais-primary shadow-lais-card'
                  : 'text-lais-text-muted hover:text-lais-text'
              "
              @click="setViewMode('side')"
            >
              <span class="i-lucide-panel-left size-4" aria-hidden="true" />
              {{ t('PROPERTIES.LIST.VIEW.SIDE') }}
            </button>
            <button
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm px-3 font-semibold transition-colors"
              :class="
                viewMode === 'map'
                  ? 'bg-white text-lais-primary shadow-lais-card'
                  : 'text-lais-text-muted hover:text-lais-text'
              "
              @click="setViewMode('map')"
            >
              <span class="i-lucide-map size-4" aria-hidden="true" />
              {{ t('PROPERTIES.LIST.VIEW.MAP') }}
            </button>
          </div>
        </div>

        <LaisCardSkeleton
          v-if="isLoading && !hasListings"
          :count="viewMode === 'map' ? 2 : 4"
          :mode="viewMode === 'side' ? 'compact' : 'card'"
        />
        <LaisErrorState
          v-else-if="errorMessage"
          data-test="imoveis-error-state"
          class="min-h-[320px]"
          :title="t('PROPERTIES.LIST.LOAD_ERROR_TITLE')"
          :description="errorMessage"
          show-action
          :action-label="t('PROPERTIES.LIST.RETRY')"
          :action-loading="isLoading"
          @action="refreshCatalog"
        />
        <LaisDiscoveryState
          v-else-if="!hasListings"
          :title="emptyStateLabel"
          :description="emptyStateDescription"
          :action-label="
            hasActiveFilters
              ? t('PROPERTIES.LIST.CLEAR_FILTERS')
              : canCreateImovel
                ? t('PROPERTIES.LIST.CREATE_FIRST')
                : ''
          "
          @action="hasActiveFilters ? clearFilters() : openCreateProperty()"
        />
        <template v-else>
          <MobileHouseList
            :cards="listingCards"
            :active-id="activeListingId"
            @open="openListingCard"
            @hover="setActiveListing"
            @toggle-select="toggleListingCard"
            @public-link="openPublicLinkForCard"
            @share="handleCardShare"
            @share-error="handleCardShareError"
            @viewed="trackListingCardViewed"
          />

          <div
            v-if="desktopListMode === 'list'"
            class="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-3"
          >
            <ListingDiscoveryCard
              v-for="card in listingCards"
              :key="card.id"
              :card="card"
              :active="String(card.id) === String(activeListingId)"
              @open="openListingCard"
              @hover="setActiveListing"
              @toggle-select="toggleListingCard"
              @public-link="openPublicLinkForCard"
              @share="handleCardShare"
              @share-error="handleCardShareError"
              @viewed="trackListingCardViewed"
            />
          </div>

          <SearchSideList
            v-else
            :cards="listingCards"
            :active-id="activeListingId"
            :show-map="showMapMode"
            @open="openListingCard"
            @hover="setActiveListing"
            @toggle-select="toggleListingCard"
            @public-link="openPublicLinkForCard"
            @share="handleCardShare"
            @share-error="handleCardShareError"
            @viewed="trackListingCardViewed"
            @map-select="card => setActiveListing(card, 'map')"
          />

          <SearchCarousel
            v-if="showMapMode"
            class="mt-4"
            :cards="listingCards"
            :active-id="activeListingId"
            @open="openListingCard"
            @hover="setActiveListing"
            @toggle-select="toggleListingCard"
            @public-link="openPublicLinkForCard"
            @share="handleCardShare"
            @share-error="handleCardShareError"
            @viewed="trackListingCardViewed"
            @active-change="card => setActiveListing(card, 'carousel')"
            @alert-click="openAlertsCenterFromDiscovery"
          />
        </template>
      </div>

      <div
        class="flex flex-col gap-3 border-t border-lais-border-soft pt-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div
          class="flex flex-wrap items-center gap-2 text-[13px] text-lais-text-muted"
        >
          <select
            v-model.number="perPage"
            class="h-8 rounded-lais-sm border border-lais-border bg-white px-2 text-lais-text focus:border-lais-primary focus:outline-none"
            @change="applyFilters"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <span>{{ t('PROPERTIES.LIST.ITEMS_PER_PAGE') }}</span>
        </div>
        <div
          class="flex flex-wrap items-center gap-3 text-[13px] text-lais-text-muted"
        >
          <span>
            {{ t('PROPERTIES.LIST.PAGE_STATUS', { page, total: totalPages }) }}
          </span>
          <button
            class="grid size-8 place-items-center rounded-lais-sm border border-lais-border text-lais-text-muted transition-colors hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="page === 1"
            :aria-label="t('PROPERTIES.LIST.PREVIOUS_PAGE')"
            @click="goToPreviousPage"
          >
            <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
          </button>
          <button
            class="grid size-8 place-items-center rounded-lais-sm border border-lais-border text-lais-text-muted transition-colors hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="page >= totalPages"
            :aria-label="t('PROPERTIES.LIST.NEXT_PAGE')"
            @click="goToNextPage"
          >
            <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div class="-mx-4 sm:-mx-6 lg:-mx-10">
        <LaisActionBar :actions="actions" />
      </div>

      <OrganizeFavoritesDialog
        mode="add"
        :show="showBulkFavoritesDialog"
        :favorites="selectedFavoriteListingItems"
        :available-lists="bulkFavoriteLists"
        :preferred-list-name="preferredFavoriteListName"
        :is-loading="bulkFavoriteSaving || bulkFavoriteListsLoading"
        @close="showBulkFavoritesDialog = false"
        @submit="submitBulkFavoriteAdd"
      />
    </div>
  </LaisOperationalPage>
</template>
